import {
  getTenantIdFromGConfig,
  getUfcShopComposedApi,
} from '@unionfab/ufc-shop-commons';
import * as S from '@unionfab/ufc-shop-commons';
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import useCNCOptions from '../../../../../../ufc-shop-commons/src/stores/quote/shop-order-quote/useCNCOptions';
import {
  loadOrderInShopEditorStore,
  ShopOrderQuoteStoreModifiers,
  useGlobalStore,
  useShopQuoteUiStore,
} from '../../../../stores';
import { NewShopQuoteDesktop } from './NewShopQuoteDesktop';
import { NewShopQuoteMobile } from './NewShopQuoteMobile';

export interface NewShopQuoteProps {
  className?: string;
  style?: Record<string, string | number>;
}

export const NewShopQuote = ({ className, style }: NewShopQuoteProps) => {
  const [spinning, toggleSpinning] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const { isDemoUser, hasAuthenticated } = useGlobalStore(g => ({
    isDemoUser: g.isDemoUser,
    hasAuthenticated: g.hasAuthenticated,
  }));
  /** 获取三笔最近更新的未支付订单 */
  const { data: unpaidOrders, isLoading } = useQuery(
    ['orders'],
    async () => {
      const resp =
        await getUfcShopComposedApi().shopOrderQueryApi.queryShopOrders({
          pageNum: 0,
          pageSize: 3,
          sortBy: 'desc(updateTime)',
          tenantId: getTenantIdFromGConfig(),
          status: ['WAIT_SUBMIT', 'WAIT_REVIEW', 'WAIT_CUSTOM_CONFIRM'],
          includes: ['sku'],
        });

      return resp.data;
    },
    { enabled: !isDemoUser && hasAuthenticated },
  );

  const navigate = useNavigate();
  const curTechCategory = useShopQuoteUiStore(s => s.curTechCategory);

  const { setOpts: setCNCOpts, options: cncOpts } = useCNCOptions();

  useEffect(() => {
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);

    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  const commonViewProps = {
    className,
    style,
  };

  if (isMobile) {
    return <NewShopQuoteMobile {...commonViewProps} />;
  }

  return (
    <NewShopQuoteDesktop
      {...commonViewProps}
      spinning={spinning}
      isDemoUser={isDemoUser}
      hasAuthenticated={hasAuthenticated}
      unpaidOrders={unpaidOrders}
      isLoading={isLoading}
      curTechCategory={curTechCategory}
      onTechCategoryChange={(_, t) => {
        useShopQuoteUiStore.setState({ curTechCategory: t });
      }}
      onBatchUploadSuccess={async (files: S.D3ModelFile[]) => {
        let storedCNCOpts = cncOpts;
        if (
          curTechCategory.type === 'CNC' &&
          Object.keys(cncOpts).length === 0
        ) {
          const res: any =
            await getUfcShopComposedApi().shopOrderQueryApi.getCNCOptions();
          setCNCOpts(res.cnc);
          storedCNCOpts = res.cnc;
        }

        /** 映射一下后端的值 */
        const techMap = new Map([
          ['3DPRINT', '3D_PRINTING'],
          ['CNC', 'CNC'],
        ]);
        try {
          // 创建新的订单
          const newOrderCode =
            await S.getUfcShopComposedApi().shopOrderUpdateApi.createShopOrder({
              requirements: { quotation: { currency: 'USD' } },
              technology: techMap.get(curTechCategory.type) as
                | '3D_PRINTING'
                | 'CNC',
            });

          toggleSpinning(true);

          await loadOrderInShopEditorStore(newOrderCode);

          // 将文件添加到 Store 内
          await ShopOrderQuoteStoreModifiers.addOrUpdateOrderItemByFile(
            newOrderCode,
            files,
            // 以回调的形式进行页面跳转防止在订单提交前页面已经跳转导致的订单更新失败
            () => {
              toggleSpinning(false);

              if (hasAuthenticated && !isDemoUser) {
                navigate(`/new_shop_quote/${newOrderCode}`);
              } else {
                navigate(`/anonymous_new_shop_quote/${newOrderCode}`);
              }
            },
            storedCNCOpts,
          );
        } catch (e) {
          toggleSpinning(false);

          console.log('>>>NewShopQuote>>>onBatchUploadSuccess', e);
        }
      }}
    />
  );
};

NewShopQuote.displayName = 'NewShopQuote';

export default NewShopQuote;
